import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateRange, PRESET_LABELS, RangePreset } from "@/lib/date-range";

/**
 * The one date-range control, shared by the Dashboard, Attendance monitor and
 * Reports. Previously each page carried its own copy, which is how they drifted
 * apart (different widths, different preset sets, only one of them flagging an
 * inverted range).
 *
 * It renders as **three bare field groups** — Period / From / To — rather than
 * its own container, so each page can drop it into an existing filter row
 * beside its own Site / Department selects and keep one consistent line of
 * controls.
 *
 * The selected range is shown **here and nowhere else**: the highlighted preset
 * plus the two date inputs are the single source of truth for "what period am I
 * looking at". Pages should not repeat it in banners, KPI hints or chart
 * subtitles — that repetition is what this component exists to end.
 *
 * Presets resolve against the **tenant's** today (BR-140/141), so "Today" means
 * the tenant's day, not the viewer's device day.
 *
 * Whatever is selected is **highlighted in place**: a chosen preset lights up
 * its chip, hand-picked dates light up the two date boxes. One selection model,
 * one visual language.
 *
 * The highlight comes from `preset` — the selection the user actually made —
 * rather than from matching `value` back against the presets. That matching is
 * lossy: on the 30th of a month "Last 30 days" and "This month" describe the
 * same dates, so one of the two chips could never light up and read as broken.
 * Pair this with `useAnchoredDateRange`, which owns that state.
 */
export function DateRangeFilter({
  value,
  preset,
  onSelectPreset,
  onChange,
  /** Width of the two date inputs; pages differ slightly in available space. */
  inputWidth = "w-40",
}: {
  value: DateRange;
  preset: RangePreset;
  onSelectPreset: (preset: Exclude<RangePreset, "custom">) => void;
  onChange: (next: DateRange) => void;
  inputWidth?: string;
}) {
  const invalid = value.from > value.to;
  /**
   * Hand-picked dates are shown by highlighting the two date boxes, exactly as
   * a chosen preset highlights its chip — one selection model, one visual
   * language. (A separate "Custom" tag sat outside that language and had to be
   * read rather than seen.) An invalid range still wins the styling, because
   * "fix this" outranks "this is selected".
   */
  const customSelected = preset === "custom";
  const inputState = invalid
    ? "border-destructive"
    : customSelected
      ? "border-primary bg-primary/5"
      : "";

  return (
    <>
      <div>
        <Label className="text-xs text-muted-foreground">Period</Label>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {PRESET_LABELS.map((p) => (
            <Button
              key={p.value}
              size="sm"
              variant={preset === p.value ? "default" : "outline"}
              // Makes the selection programmatic, not just visual.
              aria-pressed={preset === p.value}
              className="h-9"
              onClick={() => onSelectPreset(p.value)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div className={inputWidth}>
        <Label className="text-xs text-muted-foreground">From</Label>
        <Input
          type="date"
          aria-label="From date"
          className={`mt-1 h-9 ${inputState}`}
          value={value.from}
          max={value.to || undefined}
          onChange={(e) => onChange({ ...value, from: e.target.value || value.from })}
        />
      </div>

      <div className={inputWidth}>
        <Label className="text-xs text-muted-foreground">To</Label>
        <Input
          type="date"
          aria-label="To date"
          className={`mt-1 h-9 ${inputState}`}
          value={value.to}
          min={value.from || undefined}
          onChange={(e) => onChange({ ...value, to: e.target.value || value.to })}
        />
        {invalid && (
          <p className="mt-1 text-[11px] text-destructive">“From” must be on or before “To”.</p>
        )}
      </div>
    </>
  );
}
